/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * One page of the signed-in person's payments, and the optional layer that
 * turns its ids into event and activity names.
 *
 * The two are separate hooks on purpose: the money read decides whether the
 * page can be drawn at all, and the name reads decide only how a title line
 * reads. A failure in the second must never look like a failure of the first.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMyPayments, fetchEventNaming, fetchEventNamingBatch, PAGE_SIZE } from '../../../components/athlohive/myPayments';
import { fetchRegistrationActivities } from '../../../components/athlohive/event-detail/fetchMyRegistrations';
import { isUserLoggedIn } from '../../../utils/utils';

export const MY_PAYMENTS_KEY = 'myPayments';

/** One page, keyed by page and filter so paging back is instant. */
export default function useMyPayments({ page, statuses }) {
  return useQuery({
    queryKey: [MY_PAYMENTS_KEY, page, (statuses || []).join(',')],
    queryFn: () => fetchMyPayments({ page, pageSize: PAGE_SIZE, statuses }),
    staleTime: 60 * 1000,
    keepPreviousData: true,
    // A sign-in problem is answered by the session dialog, not by retrying.
    retry: (count, error) => error?.kind !== 'auth' && count < 1,
  });
}

/**
 * Event titles and booked activities for the rows on screen.
 *
 * Returns { naming, regActivities } where `naming[eventId]` is an object when
 * read, `null` when settled as unreadable, and absent while still unknown —
 * three states the title line keeps apart (see rowTitle).
 *
 * EVERY FAILURE IS SETTLED AS null AND NEVER RETRIED. A 404 on a withdrawn
 * event is the ordinary case, not an error, and asking again would only
 * repeat it. Nothing here is ever surfaced to the reader as a page error.
 */
export function usePaymentNames(rows) {
  const [naming, setNaming] = useState({});
  const [regActivities, setRegActivities] = useState({});

  // Event ids already asked for, kept OUTSIDE state and never cleared: a
  // re-render mid-read must not abandon a read that `naming` would then treat
  // as for ever in flight. Cached across pages, so paging back re-asks nothing.
  const askedRef = useRef(new Set());
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const eventIds = useMemo(() => {
    const seen = new Set();
    (rows || []).forEach((row) => { if (row.eventId) seen.add(row.eventId); });
    return Array.from(seen);
  }, [rows]);

  const regKey = useMemo(() => {
    const seen = new Set();
    (rows || []).forEach((row) => (row.registrationIds || []).forEach((id) => seen.add(id)));
    return Array.from(seen).sort().join(',');
  }, [rows]);

  useEffect(() => {
    const queue = eventIds.filter((id) => !askedRef.current.has(id));
    if (!queue.length) return;
    queue.forEach((id) => askedRef.current.add(id));

    (async () => {
      // ONE REQUEST FOR THE WHOLE PAGE when the batch read is available to
      // this reader, N when it is not. Both end in the same shape, so nothing
      // downstream knows or cares which happened.
      const batched = await fetchEventNamingBatch(queue);
      if (batched && aliveRef.current) setNaming((all) => ({ ...all, ...batched }));

      // Whatever the batch did not answer for — because it was refused
      // outright, or because one id was missing from an otherwise good reply
      // — is asked for individually. That is the pre-existing path, unchanged.
      const missing = batched ? queue.filter((id) => !(id in batched)) : queue;
      missing.forEach(async (id) => {
        const entry = await fetchEventNaming(id);
        // `null` is stored, not skipped: it is the difference between "settled
        // as unreadable" and "still waiting", and the row reads them apart.
        if (aliveRef.current) setNaming((all) => ({ ...all, [id]: entry }));
      });
    })();
  }, [eventIds]);

  useEffect(() => {
    if (!regKey) return undefined;
    let cancelled = false;
    isUserLoggedIn()
      .then((who) => (who?.user_id
        ? fetchRegistrationActivities({
          applicationUserId: who.user_id, registrationIds: regKey.split(','),
        })
        : null))
      .then((map) => { if (!cancelled && map) setRegActivities((all) => ({ ...all, ...map })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [regKey]);

  return { naming, regActivities };
}
